import { createApp } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router';
import { isString } from 'lodash';

import { Frontegg } from '@frontegg/vue';
import { useColorMode } from '@vueuse/core';

import Config from './utils/config';
import { SentryService } from './services';
import 'bootstrap';

import './style.css';
import './assets/styles/global.css';

import DialogPlugin from './plugins/dialogs.js';
import directives from './directives/';
import ClickOutside from './directives/ClickOutside/click-outside.js';
import { Permissions, Roles } from './utils/constants';
import VueSanitizeEscape from 'vue-sanitize-escape';
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import { install as VueMonacoEditorPlugin } from '@guolao/vue-monaco-editor';

const app = createApp(App);

const store = createPinia();

const allowedRedirectOrigins = isString(import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS)
    ? JSON.parse(import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS || '[]')
    : import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS || [];

// Get current color mode to set initial theme
const colorMode = useColorMode();

// Create dynamic Frontegg configuration with reactive theme
const createFronteggConfig = () => {
    const isDark = colorMode.value === 'dark';
    const themeName = isDark ? 'dark' : 'modern';
    
    console.log(`Frontegg initializing with theme: ${themeName}`);
    
    return {
        contextOptions: {
            baseUrl: Config.frontegg.baseUrl,
            clientId: Config.frontegg.clientId
        },
        authOptions: {
            keepSessionAlive: true,
            enforceRedirectToSameSite: true,
            allowedRedirectOrigins
        },
        hostedLoginBox: false,
        router,
        themeOptions: {
            adminPortal: {
                themeName
            }
        },
        localizations: {
            en: {
                errors: {
                    'ER-01013': 'Incorrect login information provided.',
                    'ER-01086': 'If the email provided is valid, you will receive an email to complete the registration process.',
                    'ER-01183':
                        'You can`t sign up. Please make sure you are using a business email address. If error persists, please contact support@streamkap.com.'
                },
                loginBox: {
                    login: {
                        emailInputLabel: 'Business Email'
                    },
                    signup: {
                        account: {
                            emailInputLabel: 'Business Email',
                            emailInputPlaceholder: 'Enter your business email',
                            successTitle: 'Email Sent',
                            successActivateMessage: 'If the email provided is valid, you will receive an email to complete the registration process.'
                        },
                        user: {
                            emailInputLabel: 'Business Email',
                            emailInputPlaceholder: 'Enter your business email',
                            successTitle: 'Email Sent',
                            successActivateMessage: 'If the email provided is valid, you will receive an email to complete the registration process.'
                        }
                    }
                }
            }
        }
    };
};

app.use(Frontegg, createFronteggConfig());

app.use(store);
app.use(router);

if (Config.app.isProd) {
    SentryService.setup(app, router);
}

console.log('APP_ENV:', Config.app.env);
console.log('APP_VERSION:', Config.app.version);

app.use(DialogPlugin);

app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            darkModeSelector: false || 'none',
            cssLayer: {
                name: 'primevue',
                order: 'theme, base, primevue'
            }
        }
    }
});

// Vue3Toastify configuration removed - now using sonner via useToast composable
// Toast configuration is now handled in App.vue with <Toaster> component

app.use(VueMonacoEditorPlugin, {
    paths: {
        vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs'
    }
});
// register all directives
directives(app);

app.directive('click-outside', ClickOutside);

app.use(VueSanitizeEscape);

// Attach the Permissions ENUM to the global properties
app.config.globalProperties.$Permissions = Permissions;
app.config.globalProperties.$Roles = Roles;

app.mount('#app');

// loadCello()
//     .then(() => {
//         console.log('Cello script loaded successfully');
//         app.mount('#app');
//     })
//     .catch((error) => {
//         console.error('Failed to load Cello script:', error);
//         app.mount('#app'); // Optionally mount the app even if the script fails to load
//     });
